import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import CreateAgreementPage from "@/pages/CreateAgreementPage";
import AgreementDetailPage from "@/pages/AgreementDetailPage";
import InvitePage from "@/pages/InvitePage";
import SettingsPage from "@/pages/SettingsPage";
// Legacy imports for backward compatibility
import CreateLoanPage from "@/pages/CreateLoanPage";
import LoanDetailPage from "@/pages/LoanDetailPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  if (user) {
    const redirectTo = new URLSearchParams(location.search).get('redirect') || '/dashboard';
    return <Navigate to={redirectTo} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />

      {/* New agreement routes */}
      <Route path="/agreements/create" element={<ProtectedRoute><CreateAgreementPage /></ProtectedRoute>} />
      <Route path="/agreements/:id" element={<ProtectedRoute><AgreementDetailPage /></ProtectedRoute>} />

      {/* Legacy loan routes - redirect to agreements */}
      <Route path="/loans/create" element={<Navigate to="/agreements/create" replace />} />
      <Route path="/loans/:id" element={<ProtectedRoute><LegacyLoanRedirect /></ProtectedRoute>} />

      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

// Component to handle legacy loan route redirects
function LegacyLoanRedirect() {
  const { id } = useParams();
  return <Navigate to={`/agreements/${id}`} replace />;
}



function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
